import React, { useState, useEffect } from 'react'
import { Sparkles, Save, BookOpen, Copy, Check, Loader2, RefreshCw, Hash, X, ChevronDown, Wand2 } from 'lucide-react'
import { youtubeApi } from '../api/client'

interface MetadataTemplate {
  id: string
  name: string
  title_pattern: string
  description_pattern: string
  tags: string[]
  created_at: string
}

interface ShortsMetadataPanelProps {
  clipTitle: string
  originalTitle: string
  transcriptText: string
  clipStart: number
  clipEnd: number
  onMetadataReady: (meta: { title: string; description: string; tags: string[] }) => void
  initialTitle?: string
  initialDescription?: string
  initialTags?: string[]
}

const TEMPLATES_STORAGE_KEY = 'legit2clip_metadata_templates'

export function ShortsMetadataPanel({
  clipTitle,
  originalTitle,
  transcriptText,
  clipStart,
  clipEnd,
  onMetadataReady,
  initialTitle = '',
  initialDescription = '',
  initialTags = [],
}: ShortsMetadataPanelProps) {
  const [title, setTitle] = useState(initialTitle || clipTitle || '')
  const [description, setDescription] = useState(initialDescription)
  const [tags, setTags] = useState<string[]>(initialTags.length > 0 ? initialTags : [])
  const [tagInput, setTagInput] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationMethod, setGenerationMethod] = useState<'llm' | 'template' | null>(null)
  const [hasStyleProfile, setHasStyleProfile] = useState(false)
  const [useStyle, setUseStyle] = useState(true)
  const [copied, setCopied] = useState<string | null>(null)
  
  // Template state
  const [templates, setTemplates] = useState<MetadataTemplate[]>([])
  const [showTemplates, setShowTemplates] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')

  // Load saved templates from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY)
      if (stored) {
        setTemplates(JSON.parse(stored))
      }
    } catch {}
    youtubeApi.getStyleProfile()
      .then((d) => setHasStyleProfile(!!d.profile))
      .catch(() => {})
  }, [])

  // Push metadata changes upstream
  useEffect(() => {
    onMetadataReady({ title, description, tags })
  }, [title, description, tags])

  const generateWithAI = async () => {
    setIsGenerating(true)
    try {
      const result = await youtubeApi.generateSmartMetadata({
        clip_title: clipTitle,
        original_title: originalTitle,
        transcript_text: transcriptText,
        clip_start: clipStart,
        clip_end: clipEnd,
        use_style: hasStyleProfile && useStyle,
      })

      if (result.success) {
        setTitle(result.title)
        setDescription(result.description)
        setTags(result.tags)
        setGenerationMethod(result.method)
      }
    } catch (err: any) {
      console.error('Metadata generation failed:', err)
    } finally {
      setIsGenerating(false)
    }
  }

  const addTag = () => {
    const tag = tagInput.trim().replace(/^#/, '')
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag])
    }
    setTagInput('')
  }

  const removeTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag))
  }

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag()
    }
  }

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopied(field)
    setTimeout(() => setCopied(null), 2000)
  }

  const saveAsTemplate = () => {
    if (!templateName.trim()) return
    const newTemplate: MetadataTemplate = {
      id: Date.now().toString(),
      name: templateName.trim(),
      title_pattern: title,
      description_pattern: description,
      tags: [...tags],
      created_at: new Date().toISOString(),
    }
    const updated = [newTemplate, ...templates]
    setTemplates(updated)
    localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(updated))
    setTemplateName('')
    setSavingTemplate(false)
  }

  const applyTemplate = (template: MetadataTemplate) => {
    setTitle(template.title_pattern)
    setDescription(template.description_pattern)
    setTags([...template.tags])
    setShowTemplates(false)
  }

  const deleteTemplate = (id: string) => {
    const updated = templates.filter(t => t.id !== id)
    setTemplates(updated)
    localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(updated))
  }

  return (
    <div className="space-y-4">
      {/* AI Generate Button */}
      <div className="flex items-center gap-2">
        <button
          onClick={generateWithAI}
          disabled={isGenerating}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Generate with AI
            </>
          )}
        </button>
        <button
          onClick={() => setShowTemplates(!showTemplates)}
          className="px-3 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors relative"
          title="Saved Templates"
        >
          <BookOpen className="w-4 h-4" />
          {templates.length > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 text-white text-[10px] rounded-full flex items-center justify-center">
              {templates.length}
            </span>
          )}
        </button>
      </div>

      {generationMethod && (
        <p className="text-xs text-gray-500 -mt-2">
          Generated via {generationMethod === 'llm' ? 'AI' : 'template'} · Edit below to customize
        </p>
      )}

      {hasStyleProfile && (
        <button
          onClick={() => setUseStyle(!useStyle)}
          className={`flex items-center gap-2 text-xs -mt-1 transition-colors ${useStyle ? 'text-indigo-300' : 'text-gray-500 hover:text-gray-400'}`}
          title="Generate titles & descriptions in your channel's voice"
        >
          <span className={`w-8 h-4 rounded-full flex items-center px-0.5 transition-colors ${useStyle ? 'bg-indigo-600 justify-end' : 'bg-gray-600 justify-start'}`}>
            <span className="w-3 h-3 bg-white rounded-full" />
          </span>
          <Wand2 className="w-3.5 h-3.5" />
          Write in my style
        </button>
      )}

      {/* Templates Dropdown */}
      {showTemplates && (
        <div className="bg-gray-900 border border-gray-600 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-white">Saved Templates</h4>
            <button onClick={() => setShowTemplates(false)} className="p-1 hover:bg-gray-700 rounded">
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>
          {templates.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-2">No saved templates yet. Generate metadata and save it as a template!</p>
          ) : (
            templates.map((t) => (
              <div key={t.id} className="flex items-center gap-2 p-2 bg-gray-800 rounded-lg hover:bg-gray-750 group">
                <button
                  onClick={() => applyTemplate(t)}
                  className="flex-1 text-left"
                >
                  <span className="text-sm text-white font-medium">{t.name}</span>
                  <span className="text-xs text-gray-500 block truncate">{t.title_pattern}</span>
                </button>
                <button
                  onClick={() => deleteTemplate(t.id)}
                  className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-600 rounded transition-all"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Title */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-gray-300">Title</label>
          <span className={`text-xs ${title.length > 100 ? 'text-red-400' : 'text-gray-500'}`}>
            {title.length}/100
          </span>
        </div>
        <div className="relative">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            placeholder="Enter a catchy title for your Short..."
            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500 pr-8"
          />
          <button
            onClick={() => copyToClipboard(title, 'title')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-700 rounded"
          >
            {copied === 'title' ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-gray-500" />}
          </button>
        </div>
      </div>

      {/* Description */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-gray-300">Description</label>
          <span className={`text-xs ${description.length > 5000 ? 'text-red-400' : 'text-gray-500'}`}>
            {description.length}/5000
          </span>
        </div>
        <div className="relative">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={5000}
            rows={4}
            placeholder="Write an engaging description with hashtags..."
            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none pr-8"
          />
          <button
            onClick={() => copyToClipboard(description, 'desc')}
            className="absolute right-2 top-2 p-1 hover:bg-gray-700 rounded"
          >
            {copied === 'desc' ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-gray-500" />}
          </button>
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="text-sm font-medium text-gray-300 mb-1 block">Tags</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-500/20 text-indigo-300 text-xs rounded-full border border-indigo-500/30"
            >
              <Hash className="w-3 h-3" />
              {tag}
              <button onClick={() => removeTag(tag)} className="hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            placeholder="Add tag (press Enter)"
            className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          />
          <button
            onClick={addTag}
            disabled={!tagInput.trim()}
            className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      {/* Save as Template */}
      {title && (
        <div className="pt-2 border-t border-gray-700">
          {savingTemplate ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name..."
                className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveAsTemplate()}
              />
              <button
                onClick={saveAsTemplate}
                disabled={!templateName.trim()}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => setSavingTemplate(false)}
                className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setSavingTemplate(true)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              Save as template
            </button>
          )}
        </div>
      )}
    </div>
  )
}
